<template>
  <div class="forge-list">
    <!-- Header -->
    <div class="forge-header">
      <div class="forge-header__left">
        <h2 class="forge-header__title">Forge Apps</h2>
        <span class="forge-header__count" v-if="items">{{ items.length }} / 150</span>
      </div>
      <div class="forge-header__right">
        <div class="forge-search">
          <i class="fa fa-search forge-search__icon"></i>
          <input
            type="text"
            class="forge-search__input"
            v-model="search"
            placeholder="Search apps…"
          />
        </div>
        <button
          class="forge-btn forge-btn--create"
          @click="$router.push('/admin/forge/create')"
          :disabled="items && items.length >= 150"
        >
          <i class="fa fa-plus"></i>
          <span>New App</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div class="forge-grid" v-if="!items">
      <div class="forge-card forge-card--skeleton" v-for="n in 4" :key="n">
        <div class="forge-card__body">
          <div class="skel skel--title"></div>
          <div class="skel skel--line"></div>
          <div class="skel skel--line skel--short"></div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div class="forge-empty" v-else-if="items.length === 0">
      <div class="forge-empty__icon">
        <i class="fa fa-cube"></i>
      </div>
      <p class="forge-empty__text">{{ $t('seemsLikeThereIsNothingHere') }}</p>
      <button class="forge-btn forge-btn--create" @click="$router.push('/admin/forge/create')">
        <i class="fa fa-plus"></i>
        <span>Create your first app</span>
      </button>
    </div>

    <!-- Grid -->
    <div class="forge-grid" v-else>
      <div
        class="forge-card"
        v-for="item in filteredItems"
        :key="item.uuid"
        @click="$router.push(`/admin/forge/${item.uuid}`)"
      >
        <div class="forge-card__header">
          <div class="forge-card__icon">
            <i class="fa fa-cube"></i>
          </div>
          <span class="forge-card__type">{{ item.type || 'unknown' }}</span>
        </div>

        <div class="forge-card__body">
          <h3 class="forge-card__name">{{ item.name }}</h3>

          <div class="forge-card__versions">
            <div class="forge-card__version">
              <span class="forge-card__version-label">Dev</span>
              <span
                class="forge-card__version-value"
                :class="{ 'is-active': item.dev_version }"
              >{{ item.dev_version || 'inactive' }}</span>
            </div>
            <div class="forge-card__version">
              <span class="forge-card__version-label">Live</span>
              <span
                class="forge-card__version-value"
                :class="{ 'is-live': item.public_version }"
              >{{ item.public_version || 'unpublished' }}</span>
            </div>
          </div>
        </div>

        <div class="forge-card__footer" v-if="item.tags && item.tags.length > 0">
          <span class="forge-card__tag" v-for="tag in item.tags" :key="tag">{{ tag }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ForgeList",
  data() {
    return {
      items: null,
      search: "",
    };
  },
  computed: {
    filteredItems() {
      if (!this.items) return [];
      if (!this.search.trim()) return this.items;
      const q = this.search.toLowerCase().trim();
      return this.items.filter((i) =>
        i.name.toLowerCase().includes(q)
      );
    },
  },
  mounted() {
    if (!this.$can("manage forge")) {
      return this.$router.back();
    }

    this.$http
      .get("/luma/appservice/v1/forge")
      .then((resp) => {
        this.items = resp.data;
      })
      .catch((err) => {
        this.$debugLog(err);
        this.$pageUnavailable();
      });
  },
};
</script>

<style scoped lang="scss">
.forge-list {
  padding: 8px 0;
}

.forge-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}

.forge-header__left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.forge-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.forge-search {
  position: relative;
  display: flex;
  align-items: center;
}

.forge-search__icon {
  position: absolute;
  left: 12px;
  font-size: 12px;
  color: var(--text-muted);
  pointer-events: none;
}

.forge-search__input {
  width: 200px;
  height: 38px;
  padding: 0 14px 0 34px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: var(--text-muted); }
  &:focus {
    border-color: var(--primary-start);
    box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.12);
  }
}

.forge-header__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.forge-header__count {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
}

// ─── Create button ─────────────────────────────
.forge-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.18s ease;

  &--create {
    padding: 9px 20px;
    background: var(--primary-start);
    color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
}

// ─── Grid ──────────────────────────────────────
.forge-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

// ─── Card ──────────────────────────────────────
.forge-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    border-color: rgba(108, 128, 255, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  }
}

.forge-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 0;
}

.forge-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(108, 128, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-start);
  font-size: 15px;
}

.forge-card__type {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--text-muted);
  background: rgba(255, 255, 255, 0.05);
  padding: 3px 10px;
  border-radius: 20px;
}

.forge-card__body {
  padding: 14px 18px 16px;
  flex: 1;
}

.forge-card__name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 14px;
  line-height: 1.3;
  word-break: break-word;
}

.forge-card__versions {
  display: flex;
  gap: 16px;
}

.forge-card__version {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.forge-card__version-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--text-muted);
}

.forge-card__version-value {
  font-size: 13px;
  color: var(--text-secondary);
  font-family: 'SFMono-Regular', 'Consolas', monospace;

  &.is-active {
    color: #57d9a3;
  }

  &.is-live {
    color: var(--primary-start);
  }
}

.forge-card__footer {
  padding: 0 18px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.forge-card__tag {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-secondary);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

// ─── Empty state ───────────────────────────────
.forge-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.forge-empty__icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(108, 128, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--text-muted);
  margin-bottom: 16px;
}

.forge-empty__text {
  font-size: 15px;
  color: var(--text-muted);
  margin: 0 0 20px;
}

// ─── Skeleton ──────────────────────────────────
.forge-card--skeleton {
  pointer-events: none;
  padding: 18px;

  .skel {
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.06);
    animation: skel-pulse 1.4s ease-in-out infinite;

    &--title {
      width: 60%;
      height: 18px;
      margin-bottom: 14px;
    }

    &--line {
      width: 80%;
      height: 12px;
      margin-bottom: 10px;
    }

    &--short {
      width: 45%;
    }
  }
}

@keyframes skel-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}
</style>
